import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { PageContainer } from "@swot/base-components";

import { rcaApi } from "../../services/rcaApi.js";
import type { RcaRecordDto } from "../../../types/rca.types.js";
import {
  CmtsRcaOwnerWorkspace,
  SEED_RCA_RECORDS,
  DetailActiveTab,
} from "./CmtsRcaOwnerWorkspace.js";
import { RcaDashboardPage } from "./pages/RcaDashboardPage.js";
import { RcaCapaActionsPage } from "./pages/RcaCapaActionsPage.js";
import { RcaReportsPage } from "./pages/RcaReportsPage.js";
import { RcaFmeaPage } from "./pages/RcaFmeaPage.js";
import { RcaSettingsPage } from "./pages/RcaSettingsPage.js";

export type CmtsRcaPanelProps = Readonly<{
  selectedRecordId?: string | undefined;
}>;

type RcaSubTabKey =
  | "DASHBOARD"
  | "RCA_RECORDS"
  | "CAPA_ACTIONS"
  | "REPORTS"
  | "FMEA"
  | "SETTINGS"
  | "INVESTIGATION";

export function CmtsRcaPanel({ selectedRecordId }: CmtsRcaPanelProps) {
  const { complaintId } = useParams<{ complaintId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active investigation subtab if navigating via Investigation Navigation sidebar
  const investigationTab: DetailActiveTab | null = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes("/investigation/overview")) return "OVERVIEW";
    if (path.includes("/investigation/method")) return "WORKSPACE";
    if (path.includes("/investigation/evidence")) return "EVIDENCE";
    if (path.includes("/investigation/actions")) return "ACTIONS";
    if (path.includes("/investigation/team")) return "TEAM";
    if (path.includes("/investigation/review")) return "REVIEW";
    if (path.includes("/investigation/audit")) return "AUDIT";
    if (
      path.includes("/investigation/fmea-feedback") ||
      path.includes("/investigation/fmea")
    )
      return "FMEA";
    if (
      path.includes("/investigation/lifecycle") ||
      path.includes("/investigation/hold")
    )
      return "HOLD_CANCEL";
    return null;
  }, [location.pathname]);

  // Determine active view directly from current sidebar route
  const activeTab: RcaSubTabKey = useMemo(() => {
    const path = location.pathname.toLowerCase();
    if (investigationTab) return "INVESTIGATION";
    if (path.includes("/dashboard")) return "DASHBOARD";
    if (
      path.includes("/capa-queue") ||
      path.includes("/capa-actions") ||
      path.includes("/capa")
    )
      return "CAPA_ACTIONS";
    if (path.includes("/reports")) return "REPORTS";
    if (path.includes("/fmea")) return "FMEA";
    if (path.includes("/settings")) return "SETTINGS";
    return "RCA_RECORDS";
  }, [location.pathname, investigationTab]);

  // Fetch live RCA records if backend is active, or use rich fallback
  const listQuery = useQuery({
    queryKey: ["cmts", "rca", complaintId || "queue"],
    queryFn: async () => {
      if (complaintId) {
        return rcaApi.list(complaintId);
      }
      return rcaApi.queue();
    },
    retry: 1,
  });

  const apiRecords = (listQuery.data as RcaRecordDto[] | undefined) || [];
  const records = apiRecords;

  const pageTitle = useMemo(() => {
    switch (activeTab) {
      case "DASHBOARD":
        return "RCA / CAPA Dashboard";
      case "CAPA_ACTIONS":
        return "CAPA Actions & Provenance";
      case "REPORTS":
        return "RCA Analytics & Reports";
      case "FMEA":
        return "FMEA Risk Feedback Registry";
      case "SETTINGS":
        return "RCA Module Settings";
      case "INVESTIGATION":
        return "RCA Investigation Workspace";
      default:
        return "Root Cause Analysis (RCA)";
    }
  }, [activeTab]);

  const pageSubtitle = useMemo(() => {
    switch (activeTab) {
      case "DASHBOARD":
        return "Real-time investigation progress, SLA countdowns, and root-cause verification analytics.";
      case "CAPA_ACTIONS":
        return "Track corrective and preventive actions handed over from RCA with Complaint provenance linkage.";
      case "REPORTS":
        return "Pareto defect analysis, MTTR resolution benchmarks, and recurrence prevention reports.";
      case "FMEA":
        return "AIAG & VDA Action Priority (AP) tracking to automatically update Process/Design FMEAs.";
      case "SETTINGS":
        return "Configure SLA thresholds, enabled RCA methods, separation-of-duties rules, and quality gates.";
      case "INVESTIGATION":
        return "Deep-dive root cause investigation, method analysis, evidence integrity, and verification handoff.";
      default:
        return "Governed root cause investigation methods, evidence center, and verification workspaces.";
    }
  }, [activeTab]);

  return (
    <PageContainer title={pageTitle} subtitle={pageSubtitle}>
      <div className="space-y-4">
        {/* Dedicated Standalone Page View matching Sidebar Selection */}
        {activeTab === "DASHBOARD" && (
          <RcaDashboardPage
            records={records}
            onSelectRecord={(record) =>
              navigate(`/cmts/rca-capa/rca/${record.id}`)
            }
            onCreateNew={() => navigate("/cmts/rca-capa/rca-queue")}
          />
        )}

        {activeTab === "RCA_RECORDS" && (
          <CmtsRcaOwnerWorkspace
            initialRecords={records}
            selectedRecordId={selectedRecordId}
            complaintId={complaintId}
            onRecordCreated={() => {
              void listQuery.refetch();
            }}
            isLoading={listQuery.isLoading}
          />
        )}

        {activeTab === "INVESTIGATION" && (
          <CmtsRcaOwnerWorkspace
            initialRecords={records}
            selectedRecordId={selectedRecordId}
            complaintId={complaintId}
            initialDetailTab={investigationTab || "OVERVIEW"}
            onRecordCreated={() => {
              void listQuery.refetch();
            }}
            isLoading={listQuery.isLoading}
          />
        )}

        {activeTab === "CAPA_ACTIONS" && <RcaCapaActionsPage />}

        {activeTab === "REPORTS" && <RcaReportsPage />}

        {activeTab === "FMEA" && <RcaFmeaPage />}

        {activeTab === "SETTINGS" && <RcaSettingsPage />}
      </div>
    </PageContainer>
  );
}
